import { useCallback } from "react";
import { toast } from "react-toastify";
import { sendWebSocketMessage } from "../../../hooks/useWebSocket";
import {
  getPayloadSize,
  payloadQualifiesForS3Upload,
} from "../../../component/Methods/checks";
import { uploadToS3 } from "../../../utils/s3Utils";
import {
  GET_SSANDC_FILLS_REQUEST,
  ALLOCATE_SSANDC_TRADES_REQUEST,
  CANCEL_SSANDC_TRADES_REQUEST,
  getSsandcRouterId,
  abbreviationIncludesSsandc,
  routerDisplayIncludesSsandc,
  findRouteByRouterId,
  isSsandcRoutedSelectionItem,
  isSsandcRoutedOnlySelectionItem,
  getTradeIdsFromSelection,
  isCommissionEligibleTrade,
  buildCommissionPayloadEntry,
} from "../tradeBlotterHelpers";

const TOAST = { position: "top-right", autoClose: 5000 };

export function useTradeBlotterActions({
  selectedTrades,
  searchFilteredData,
  trades,
  selectedDate,
  brokers,
  routes,
  apiInfo,
  userInfo,
  startLoadingRequest,
  setRouteDialogOpen,
}) {
  const handleUpdateFills = useCallback(() => {
    const ssandcRouterId = getSsandcRouterId(routes);
    const tradeIds = searchFilteredData
      .filter((row) => routerDisplayIncludesSsandc(row.routerDisplay))
      .map((row) => String(row.tradeId));

    if (tradeIds.length === 0) {
      toast.info("No ss&c trades are available to refresh fills.");
      return;
    }

    startLoadingRequest(GET_SSANDC_FILLS_REQUEST, "Refreshing SS&C fills...");
    sendWebSocketMessage(
      JSON.stringify({
        action: GET_SSANDC_FILLS_REQUEST,
        data: { router: String(ssandcRouterId) },
      })
    );
  }, [searchFilteredData, routes, startLoadingRequest]);

  const handleRouteSubmit = useCallback(
    (routerId) => {
      if (!selectedTrades?.length) {
        toast.error("Please select at least one trade to route");
        return;
      }
      if (!routerId) {
        toast.error("Please select a router");
        return;
      }

      const eligible = selectedTrades.filter((t) => t?.tradeObject?.router == null);
      if (eligible.length === 0) {
        toast.info("Selected trades are already routed.");
        return;
      }

      const tradeIds = getTradeIdsFromSelection(eligible);
      const selectedRoute = findRouteByRouterId(routes, routerId);
      const isSsAndC = abbreviationIncludesSsandc(
        selectedRoute?.abbreviation?.toLowerCase() || ""
      );
      const action = isSsAndC ? "route_ssandc" : "request_route_order";

      sendWebSocketMessage(
        JSON.stringify({
          action,
          data: { trades: tradeIds, router: String(routerId) },
        })
      );
      startLoadingRequest(
        isSsAndC ? "route_ssandc" : "route_orders",
        isSsAndC ? "Routing SS&C trades..." : "Routing EMSX trades..."
      );
      setRouteDialogOpen(false);
    },
    [selectedTrades, routes, startLoadingRequest, setRouteDialogOpen]
  );

  const handleAllocationSubmit = useCallback(() => {
    if (!selectedTrades?.length) {
      toast.error("Please select at least one trade to allocate");
      return;
    }

    const eligible = selectedTrades.filter((t) =>
      isSsandcRoutedSelectionItem(t, routes)
    );
    if (eligible.length === 0) {
      toast.info("No SS&C routed trades with unset allocation in selection to allocate.");
      return;
    }

    startLoadingRequest(ALLOCATE_SSANDC_TRADES_REQUEST, "Sending SS&C trade allocations...");
    sendWebSocketMessage(
      JSON.stringify({
        action: ALLOCATE_SSANDC_TRADES_REQUEST,
        data: { trades: getTradeIdsFromSelection(eligible) },
      })
    );
  }, [selectedTrades, routes, startLoadingRequest]);

  const handleCancelTradesSubmit = useCallback(() => {
    if (!selectedTrades?.length) {
      toast.error("Please select at least one trade to cancel");
      return;
    }

    const eligible = selectedTrades.filter((t) =>
      isSsandcRoutedOnlySelectionItem(t, routes)
    );
    if (eligible.length === 0) {
      toast.error(
        "Selected trades are not routed via SS&C. Only SS&C routed trades can be cancelled."
      );
      return;
    }

    startLoadingRequest(CANCEL_SSANDC_TRADES_REQUEST, "Cancelling SS&C trades...");
    sendWebSocketMessage(
      JSON.stringify({
        action: CANCEL_SSANDC_TRADES_REQUEST,
        data: { trades: getTradeIdsFromSelection(eligible) },
      })
    );
  }, [selectedTrades, routes, startLoadingRequest]);

  const handleSubmitFills = useCallback(
    async (fillData) => {
      try {
        startLoadingRequest("modify_trade_fills", "Updating fills...");
        sendWebSocketMessage(
          JSON.stringify({ action: "modify_trade_fills", data: [fillData] })
        );
        return Promise.resolve();
      } catch (error) {
        console.error("Error submitting fills:", error);
        return Promise.reject(error);
      }
    },
    [startLoadingRequest]
  );

  const updateCommissionForEligibleTrades = useCallback(async () => {
    if (!Array.isArray(trades) || trades.length === 0) return;

    const eligibleData = trades.filter((item) =>
      isCommissionEligibleTrade(item, selectedDate, brokers)
    );
    if (eligibleData.length === 0) {
      toast.error("No eligible trades found with Cantor or Goldman Sachs brokers");
      return;
    }

    const webSocketPayload = {
      action: "modify_trade_commissions",
      data: eligibleData.map((trade) => buildCommissionPayloadEntry(trade, brokers)),
    };

    if (payloadQualifiesForS3Upload(getPayloadSize(webSocketPayload))) {
      try {
        const s3Result = await uploadToS3(
          webSocketPayload,
          apiInfo,
          userInfo,
          "modify_trade_commissions"
        );
        if (!s3Result.success) {
          toast.error("Failed to upload commission data to S3. Please try again later.");
          return;
        }
        sendWebSocketMessage(
          JSON.stringify({ action: "modify_trade_commissions", file: s3Result.fileName })
        );
      } catch {
        toast.error("Failed to upload commission data to S3. Please try again later.");
        return;
      }
    } else {
      sendWebSocketMessage(JSON.stringify(webSocketPayload));
    }

    startLoadingRequest("modify_trade_commissions", "Auto Filling Broker Commissions...");
  }, [trades, selectedDate, brokers, apiInfo, userInfo, startLoadingRequest]);

  const handleArchiveSelected = useCallback(() => {
    const anyUnfilled = selectedTrades.some((trade) => trade.filledPercent < 100);
    if (anyUnfilled) {
      toast.error("Cannot archive unfilled trades.", TOAST);
      return false;
    }
    return true;
  }, [selectedTrades]);

  return {
    handleUpdateFills,
    handleRouteSubmit,
    handleAllocationSubmit,
    handleCancelTradesSubmit,
    handleSubmitFills,
    updateCommissionForEligibleTrades,
    handleArchiveSelected,
  };
}
